import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxState } from "..";
import {
  getTagLevelIntegrationsByTagLevel,
  createTagLevelIntegration,
  deleteTagLevelIntegrationByIds,
} from "../../docuclipper/api";
import { Integration, TagLevelIntegration } from "../../docuclipper/DocuclipperTypes";

// Supported integration scopes
export const SUPPORTED_SCOPES = {
  quickbooks: "intuit.quickbooks.accounting",
  xero: "xero.accounting",
};

interface TagLevelIntegrationState {
  // The linked integration for the current tag level
  linkedIntegration: TagLevelIntegration | null;
  // The full integration object (from Integration reducer)
  linkedIntegrationDetails: Integration | null;
  // Loading states
  loading: boolean;
  saving: boolean;
  // Error state
  error: string | null;
  // Track if we've fetched for the current tag level
  fetchedForTagLevelId: number | null;
}

const initialState: TagLevelIntegrationState = {
  linkedIntegration: null,
  linkedIntegrationDetails: null,
  loading: false,
  saving: false,
  error: null,
  fetchedForTagLevelId: null,
};

const slice = createSlice({
  name: "TagLevelIntegration",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setSaving(state, action: PayloadAction<boolean>) {
      state.saving = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setLinkedIntegration(
      state,
      action: PayloadAction<{
        integration: TagLevelIntegration | null;
        details: Integration | null;
        tagLevelId: number | null;
      }>
    ) {
      state.linkedIntegration = action.payload.integration;
      state.linkedIntegrationDetails = action.payload.details;
      state.fetchedForTagLevelId = action.payload.tagLevelId;
      state.loading = false;
    },
    clearLinkedIntegration(state) {
      state.linkedIntegration = null;
      state.linkedIntegrationDetails = null;
      state.fetchedForTagLevelId = null;
    },
  },
});

export const {
  setLoading,
  setSaving,
  setError,
  setLinkedIntegration,
  clearLinkedIntegration,
} = slice.actions;

// Helper to get current tag level ID from state
const getCurrentTagLevelId = (state: ReduxState): number | null => {
  const { tagLevels, selectedL1, selectedL2 } = state.TagLevel;

  if (!selectedL1 || !selectedL2 || !tagLevels?.root) {
    return null;
  }

  const tagId = (tagLevels.root as any)?.[selectedL1]?.[selectedL2]?.metadata?.tagId;
  return tagId ? parseInt(tagId, 10) : null;
};

// Helper to find supported integration from list
const findSupportedIntegration = (
  integrations: TagLevelIntegration[],
  allIntegrations: Integration[]
): { tagLevelIntegration: TagLevelIntegration; integration: Integration } | null => {
  for (const tli of integrations) {
    // First check if Integration is embedded in the response
    if (tli.integration && Object.values(SUPPORTED_SCOPES).includes(tli.integration.scope)) {
      return { tagLevelIntegration: tli, integration: tli.integration };
    }

    // Fall back to looking up from allIntegrations
    // Use String() to handle both string and number IDs
    const integration = allIntegrations.find(
      (i) => String(i.id) === String(tli.integrationId)
    );
    if (integration && Object.values(SUPPORTED_SCOPES).includes(integration.scope)) {
      return { tagLevelIntegration: tli, integration };
    }
  }
  return null;
};

// Thunk: Fetch the linked integration for current tag level
export const fetchTagLevelIntegration = () => async (dispatch, getState) => {
  const state: ReduxState = getState();
  const tagLevelId = getCurrentTagLevelId(state);
  const { integrations } = state.Integration;

  // If no tag level selected, clear and return
  if (!tagLevelId) {
    dispatch(setLinkedIntegration({ integration: null, details: null, tagLevelId: null }));
    return;
  }

  // Skip if already fetched for this tag level
  if (state.TagLevelIntegration.fetchedForTagLevelId === tagLevelId) {
    return;
  }

  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const data = await getTagLevelIntegrationsByTagLevel(tagLevelId);
    const found = findSupportedIntegration(data, integrations);

    dispatch(
      setLinkedIntegration({
        integration: found?.tagLevelIntegration || null,
        details: found?.integration || null,
        tagLevelId,
      })
    );
  } catch (err: any) {
    console.error("Error fetching tag level integration:", err);
    // Don't show error for 404 - endpoint might not exist yet
    if (!err.message?.includes("404") && !err.message?.includes("Not Found")) {
      dispatch(setError(err.message || "Failed to load integration"));
    }
    dispatch(setLinkedIntegration({ integration: null, details: null, tagLevelId }));
  }
};

// Thunk: Link an integration to the current tag level
export const linkIntegration = (integrationId: number, explicitTagLevelId?: number) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  const tagLevelId = explicitTagLevelId || getCurrentTagLevelId(state);
  const { integrations } = state.Integration;

  if (!tagLevelId) {
    dispatch(setError("No project selected"));
    return false;
  }

  dispatch(setSaving(true));
  dispatch(setError(null));

  try {
    const result = await createTagLevelIntegration({
      tagLevelId,
      integrationId,
      metadata: { syncEnabled: true },
    });

    // Find the integration details
    const integration = integrations.find((i) => String(i.id) === String(integrationId));

    dispatch(
      setLinkedIntegration({
        integration: result,
        details: integration || null,
        tagLevelId,
      })
    );
    dispatch(setSaving(false));
    return true;
  } catch (err: any) {
    console.error("Error linking integration:", err);
    dispatch(setError(err.message || "Failed to connect integration"));
    dispatch(setSaving(false));
    return false;
  }
};

// Thunk: Unlink the integration from the current tag level
export const unlinkIntegration = () => async (dispatch, getState) => {
  const state: ReduxState = getState();
  const tagLevelId = getCurrentTagLevelId(state);
  const { linkedIntegration } = state.TagLevelIntegration;

  if (!tagLevelId || !linkedIntegration) {
    return false;
  }

  dispatch(setSaving(true));
  dispatch(setError(null));

  try {
    await deleteTagLevelIntegrationByIds(tagLevelId, linkedIntegration.integrationId);

    dispatch(
      setLinkedIntegration({
        integration: null,
        details: null,
        tagLevelId,
      })
    );
    dispatch(setSaving(false));
    return true;
  } catch (err: any) {
    console.error("Error unlinking integration:", err);
    dispatch(setError(err.message || "Failed to disconnect integration"));
    dispatch(setSaving(false));
    return false;
  }
};

export default slice.reducer;

